import React, {FC, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {TableFilter, TablePagination, TnReactTable, useDatatable} from '@touchnet/react-table';
import ColumnSelectCell from '../general/tables/column-select-cell';
import ColumnSelectHeader from '../general/tables/column-select-header';
import {
    RowSelectionState,
    createColumnHelper,
    getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel,
} from '@tanstack/react-table';
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';

import {Term} from '~/types/term-type';
import Selectors from "~/state/selectors.ts";
import {actions} from "~/state/reducers/term-reducer.ts";

interface ComponentProps {
    isNewTermsModalOpen: boolean;
    toggleModalOpen: () => void;
}

export const TermsForImport: FC<ComponentProps> = ({isNewTermsModalOpen, toggleModalOpen}: ComponentProps) => {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const dispatch = useDispatch();
    const terms: Term[] = useSelector(Selectors.getNewTerms);

    const importAndActivateTerms = () => {
        importTerms(true);
    };

    const importAndDeactivateTerms = () => {
        importTerms(false);
    };

    const importTerms = (activate: boolean) => {
        if (Object.keys(rowSelection).length === 0) {
            return;
        }

        const termsToImport: Term[] = [];

        for (const termId of Object.keys(rowSelection)) {
            const term: Term = terms.find(term => term.id === termId);
            if (term !== undefined) {
                termsToImport.push({
                    ...term,
                    enabled: activate
                });
            }
        }

        dispatch(actions.updateTerms(termsToImport));

        setRowSelection({});
        toggleModalOpen();
    };

    const showModal = isNewTermsModalOpen && (terms.length !== 0);

    // TODO: remove this local date function and pull in formatDate 
    // from react-intl with useIntl() in the same way that Advisor does.
    const formatDate = (ts: number) => {
        if (!ts) {
            return '';
        }

        const date = new Date(Number(ts));
        return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
    };

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<Term>();
        return [
            columnHelper.display({
                id: 'select',
                header: ({table}) => <ColumnSelectHeader table={table} />,
                cell: ({row}) => <ColumnSelectCell row={row} />,
            }),
            columnHelper.accessor('code', {
                header: 'Code',
                sortingFn: 'basic',
            }),
            columnHelper.accessor('description', {
                header: 'Description',
                sortingFn: 'basic',
            }),
            columnHelper.accessor(row => formatDate(row.startDateMillis), {
                id: 'startDateMillis',
                header: 'Start Date',
                sortingFn: 'numberBoolean',
            }),
            columnHelper.accessor(row => formatDate(row.endDateMillis), {
                id: 'endDateMillis',
                header: 'End Date',
                sortingFn: 'numberBoolean',
            }),
        ];
    }, []);

    const table = useDatatable({
        columns: columns,
        data: terms,
        enableRowSelection: true,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onRowSelectionChange: setRowSelection,
        getRowId: row => row.id,
        state: {
            rowSelection
        }
    });

    return (
        <Modal isOpen={showModal} size="xl">
            <ModalHeader toggle={toggleModalOpen}>New Terms Available</ModalHeader>
            <ModalBody>
                <div className="table-search-right">
                    <TableFilter table={table} />
                </div>
                <TnReactTable table={table} noDataText={'No Terms Found'} />
                <TablePagination table={table} />
            </ModalBody>
            <ModalFooter>
                <Button className="btn-default" onClick={toggleModalOpen}>Cancel</Button>
                <Button className="btn-default" onClick={importAndDeactivateTerms}>Import</Button>
                <Button color="primary" onClick={importAndActivateTerms}>Import and Activate</Button>
            </ModalFooter>
        </Modal>
    );
}

export default TermsForImport;
